import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push, RouterState } from 'connected-react-router';

import { GlossaryState } from 'common/modules/app/glossaries';
import { ContractGL, Glossary } from 'common/utils/glossary';

import {
  InitializeState,
  InitializeStatus
} from 'common/modules/app/initialize';
import { AppState } from 'pages';
import { PurchaseState, PurchaseStatus } from 'common/modules/app/purchase';
import { PurchaseHeader } from 'pages/purchase/components/PurchaseHeader';
import { default as ContractPlanLists } from './ContractPlanLists';
import { default as PurchaseHistoryLists } from './PurchaseHistoryLists';
import {
  setStatus,
  PurchaseStatus as PurchasePageStatus
} from 'common/modules/app/purchase';
import { Principal } from 'common/modules/app/principal';
import { getLogin } from 'common/modules/domain/user';
import { getPurchaseUrl } from 'common/utils/urls';

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const initState = useSelector<AppState, InitializeState>(
    (state) => state.app.initialize
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const purchaseState = useSelector<AppState, PurchaseState>(
    (state) => state.app.purchase
  );
  const routerState = useSelector<AppState, RouterState>(
    (state) => state.app.router
  );
  const principalState = useSelector<AppState, Principal>(
    (state) => state.app.principal
  );
  return {
    init: initState,
    selectedPurchase: purchaseState.status,
    router: routerState,
    isLoggedIn: principalState.isLoggedIn,
    glossary: Glossary.instance(glState)
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const _searchParams = new URLSearchParams(state.router.location.search);
  const _queryPurchase = _searchParams.get('purchase') || '';

  /** 初期化処理 */
  const init = () => {
    // 未ログインの場合はログインさせる
    if (!state.isLoggedIn) {
      dispatch(getLogin());
    } else if (_queryPurchase === 'contract') {
      // クエリーパラメータが指定されている場合はpushで再遷移
      dispatch(setStatus(PurchasePageStatus.CONTRACT_DISP));
      dispatch(push(getPurchaseUrl()));
    } else if (_queryPurchase === 'history') {
      // クエリーパラメータが指定されている場合はpushで再遷移
      dispatch(setStatus(PurchasePageStatus.HISTORY_DISP));
      // 履歴画面でもクエリ付いた状態のまま
      dispatch(push(getPurchaseUrl()));
    }
  };

  /** 購入履歴ボタンを押下した際の処理 */
  const onClickHistory = () => {
    // 選択した画面をStateに設定
    dispatch(setStatus(PurchaseStatus.HISTORY_DISP));
  };

  /** 契約中ボタンを選択した際の処理 */
  const onClickContract = () => {
    // 選択した画面をStateに設定
    dispatch(setStatus(PurchaseStatus.CONTRACT_DISP));
  };

  /** 契約中のプラン一覧と購入履歴でリストを作成 */
  const _selected_contract =
    state.selectedPurchase === PurchaseStatus.CONTRACT_DISP ? true : false;
  const _selected_history =
    state.selectedPurchase === PurchaseStatus.HISTORY_DISP ? true : false;
  const _purchaseList = [
    {
      id: PurchaseStatus.CONTRACT_DISP,
      text: state.glossary.glx(ContractGL.Contract),
      select: _selected_contract,
      onClick: onClickContract
    },
    {
      id: PurchaseStatus.HISTORY_DISP,
      text: state.glossary.glx(ContractGL.History),
      select: _selected_history,
      onClick: onClickHistory
    }
  ];
  return {
    items: _purchaseList,
    initialize: init,
    selectedPurchase: state.selectedPurchase
  };
};

/**
 * 契約中プラン一覧、購入履歴画面全体のContainer Component
 */
export default function ContainerComponent() {
  const _props = { ...useStateProps(), ...useMargeProps(useStateProps()) };
  const isCompleteInit = _props.init.status === InitializeStatus.FINISH;

  React.useMemo(() => {
    if (isCompleteInit) {
      _props.initialize();
    }
  }, [isCompleteInit]);

  const list = React.useMemo(() => {
    if (_props.selectedPurchase === PurchaseStatus.HISTORY_DISP) {
      return <PurchaseHistoryLists />;
    } else {
      return <ContractPlanLists />;
    }
  }, [_props.selectedPurchase]);

  return isCompleteInit ? (
    <section id="purchasePage" data-test="purchasePage">
      <PurchaseHeader {..._props} />
      {list}
    </section>
  ) : null;
}
